// Step 1: upload + parse. Supports csv/tsv/txt (Papa Parse) and xls/xlsx (read-excel-file).

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, UploadCloud } from 'lucide-react';
import { Button } from '~/components/ui';
import { generateCsvTemplate } from '~/utils';
import {
  ACCEPTED_EXTENSIONS,
  NO_HEADERS_ERROR_KEY,
  parseFile,
} from '~/pages/inventory-import/lib/parseFile';
import {
  canExtractSpreadsheet,
  looksLikeFlatTable,
  needsExtraction,
  EXTRACTION_EXTENSIONS,
} from '~/pages/inventory-import/lib/uploadRouter';
import type { ParsedFile } from '~/pages/inventory-import/lib/types';
import { isTooLargeToUpload, MAX_UPLOAD_SIZE_MB } from '~/utils/uploadError';

const TEMPLATE_HEADERS = [
  'Product',
  'Length',
  'Location',
  'Stock',
  'Lead Time (Days)',
  'Replenishment Rate Per Week',
  'Minimum Order Quantity',
  'Critical Stock Level',
  'Low Stock Level',
  'Unit',
];

interface UploadStepProps {
  onParsed: (file: ParsedFile) => void;
  onExtract?: (file: File) => void;
  /** A message from outside this step, such as the server refusing an upload. */
  externalError?: string | null;
  disabled?: boolean;
}

export function UploadStep({
  onParsed,
  onExtract,
  externalError = null,
  disabled = false,
}: UploadStepProps) {
  const { t } = useTranslation('inventory-import');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const accepted: readonly string[] = onExtract
    ? [...ACCEPTED_EXTENSIONS, ...EXTRACTION_EXTENSIONS]
    : ACCEPTED_EXTENSIONS;
  const shownError = error ?? externalError;

  const extract = (file: File) => {
    if (isTooLargeToUpload(file)) {
      setError(
        t('general:drag_and_drop.size_error', { maxSizeMB: MAX_UPLOAD_SIZE_MB })
      );
      return;
    }
    onExtract?.(file);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled) return;
    setBusy(true);
    setError(null);
    const extractable = onExtract && canExtractSpreadsheet(file.name);
    try {
      if (onExtract && needsExtraction(file.name)) {
        extract(file);
        return;
      }
      const parsed = await parseFile(file);
      if (extractable && !looksLikeFlatTable(parsed)) {
        extract(file);
        return;
      }
      onParsed(parsed);
    } catch (e) {
      if (
        extractable &&
        e instanceof Error &&
        e.message === NO_HEADERS_ERROR_KEY
      ) {
        extract(file);
        return;
      }
      // parseFile throws an Error whose message is a translation key; any other failure
      // falls back to the generic parse-error message.
      setError(
        e instanceof Error
          ? t(e.message, { defaultValue: t('upload.error_parse') })
          : t('upload.error_parse')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFile(e.dataTransfer.files[0]);
        }}
        className={`flex h-52 w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed text-sm transition-colors ${
          dragging
            ? 'border-prim bg-prim-50'
            : 'border-neut-border bg-neut-bg hover:bg-neut-hover'
        }`}
      >
        <UploadCloud size={28} className="text-neut-sec" />
        <span className="font-medium text-neut-prim">
          {busy ? t('upload.parsing') : t('upload.drop_or_browse')}
        </span>
        <span className="text-xs text-neut-sec">
          {t('upload.supported', { types: accepted.join(', ') })}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        aria-label={t('upload.input_aria')}
        accept={accepted.join(',')}
        className="hidden"
        onChange={(e) => {
          // Reset the input value so re-selecting the SAME file fires onChange again — e.g. after a
          // parse error, picking the same corrected file (same name) would otherwise be ignored.
          // The File reference is already captured by handleFile's argument before we clear it.
          void handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {shownError && (
        <p
          data-testid="upload-error"
          className="rounded-md bg-error-50 px-3 py-2 text-sm text-error-600"
        >
          {shownError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          width="auto"
          onClick={() =>
            generateCsvTemplate(
              TEMPLATE_HEADERS,
              'inventory_import_template.csv'
            )
          }
        >
          <Download size={16} />
          {t('upload.download_template')}
        </Button>
        <span className="text-xs text-neut-sec">
          {t('upload.required_columns_note')}
        </span>
      </div>

      <div className="rounded-lg border border-neut-border bg-neut-bg-alt p-3 text-xs text-neut-sec">
        <p className="mb-1 font-medium text-neut-prim">
          {t('upload.supported_types_title')}
        </p>
        <ul className="list-disc pl-5">
          <li>
            <strong>{t('upload.supported_types.delimited_label')}</strong>{' '}
            {t('upload.supported_types.delimited_desc')}
          </li>
          <li>
            <strong>{t('upload.supported_types.spreadsheet_label')}</strong>{' '}
            {t('upload.supported_types.spreadsheet_desc')}
          </li>
          <li>
            <strong>{t('upload.supported_types.document_label')}</strong>{' '}
            {t('upload.supported_types.document_desc')}
          </li>
        </ul>
      </div>
    </div>
  );
}
